import { useQuery } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import { EmptyState } from '../components/ui/EmptyState'
import { Icon } from '../components/ui/Icon'
import { GovernancePulse } from '../governance/GovernancePulse'
import { MyGovernanceTasks } from '../governance/MyGovernanceTasks'
import { getGovernancePulse } from '../governance/governance-api'
import { getOnboardingRecommendation } from '../onboarding/onboarding-recommendation'
import { loadOnboardingState } from '../onboarding/onboarding-api'
import { getP4Strategy, listP4Initiatives } from '../portfolio/p4-api'
import { getRegisterSummary, listRegisterEntries } from '../register/register-api'
import { getAiRiskReportingSummary } from '../risk/risk-api'
import { useTenant } from '../tenancy/TenantProvider'
import { ManagementAttentionDashboard } from '../reporting/ManagementAttentionDashboard'

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function count(value: unknown): number { return typeof value === 'number' ? value : Number(value) || 0 }

export function GovernanceOverviewPage() {
  const { hasRole } = useTenant()
  if (hasRole('governance_manager') || hasRole('executive_viewer')) return <ManagementAttentionDashboard />
  return <LegacyGovernanceOverviewPage />
}

function LegacyGovernanceOverviewPage() {
  const { current, hasModule, hasRole } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const hasRegister = hasModule('ai_register_product_library')
  const hasPortfolio = hasModule('strategy_roadmap')
  const canReadPortfolio = Boolean(current)
  const canReadOnboarding = hasRole('organisation_admin') || hasRole('governance_manager')

  const registerSummaryQuery = useQuery({
    queryKey: ['overview-register-summary', organisationId],
    queryFn: () => getRegisterSummary(organisationId),
    enabled: Boolean(organisationId && hasRegister),
  })
  const registerEntriesQuery = useQuery({
    queryKey: ['overview-register-entries', organisationId],
    queryFn: () => listRegisterEntries({ organisationId }),
    enabled: Boolean(organisationId && hasRegister),
  })
  const governancePulseQuery = useQuery({
    queryKey: ['phase3-governance-pulse', organisationId],
    queryFn: () => getGovernancePulse(organisationId),
    enabled: Boolean(organisationId && hasRegister),
  })
  const riskSummaryQuery = useQuery({
    queryKey: ['overview-ai-risk-summary', organisationId],
    queryFn: () => getAiRiskReportingSummary(organisationId),
    enabled: Boolean(organisationId && hasRegister),
  })
  const strategyQuery = useQuery({
    queryKey: ['overview-strategy', organisationId],
    queryFn: () => getP4Strategy(organisationId),
    enabled: Boolean(organisationId && hasPortfolio && canReadPortfolio),
  })
  const initiativesQuery = useQuery({
    queryKey: ['overview-initiatives', organisationId],
    queryFn: () => listP4Initiatives(organisationId),
    enabled: Boolean(organisationId && hasPortfolio && canReadPortfolio),
  })
  const onboardingQuery = useQuery({
    queryKey: ['overview-onboarding', organisationId],
    queryFn: () => loadOnboardingState(organisationId),
    enabled: Boolean(organisationId && canReadOnboarding),
  })

  if (!current) return <Navigate to="/workspace/create" replace />

  const summary = registerSummaryQuery.data
  const riskSummary = riskSummaryQuery.data ?? {}
  const entries = registerEntriesQuery.data ?? []
  const strategyVersions = strategyQuery.data?.versions ?? []
  const currentStrategy = strategyVersions.find((version) => version.status === 'current')
  const initiatives = initiativesQuery.data ?? []
  const activeInitiatives = initiatives.filter((initiative) => !['delivered', 'cancelled', 'archived'].includes(initiative.stage))
  const inProgressInitiatives = initiatives.filter((initiative) => initiative.stage === 'in_progress')
  const attentionEntries = entries
    .filter((entry) => entry.governanceAttention === 'attention_needed' || entry.ownerOrInformationMissing)
    .sort((a, b) => {
      if (a.governanceAttention === 'attention_needed' && b.governanceAttention !== 'attention_needed') return -1
      if (b.governanceAttention === 'attention_needed' && a.governanceAttention !== 'attention_needed') return 1
      return new Date(b.lastMaterialUpdateAt).getTime() - new Date(a.lastMaterialUpdateAt).getTime()
    })
    .slice(0, 4)
  const recentEntries = [...entries]
    .sort((a, b) => new Date(b.lastMaterialUpdateAt).getTime() - new Date(a.lastMaterialUpdateAt).getTime())
    .slice(0, 4)

  const anyLoadError = [registerSummaryQuery.error, registerEntriesQuery.error, governancePulseQuery.error, riskSummaryQuery.error, strategyQuery.error, initiativesQuery.error, onboardingQuery.error].some(Boolean)
  const registerIsEmpty = hasRegister && !registerSummaryQuery.isLoading && (summary?.total ?? 0) === 0
  const onboardingRecommendation = getOnboardingRecommendation(onboardingQuery.data)
  const onboardingRecommendationAvailable = onboardingRecommendation
    ? onboardingRecommendation.key === 'governance_owner'
      || (onboardingRecommendation.key === 'known_ai_uses' && hasRegister)
      || (onboardingRecommendation.key === 'first_ai_use' && hasRegister)
      || (onboardingRecommendation.key === 'strategy' && hasPortfolio && canReadPortfolio)
    : false
  const showOnboardingRecommendation = Boolean(
    onboardingRecommendation
      && onboardingRecommendationAvailable
      && onboardingQuery.data?.organisation.onboardingStatus === 'baseline_complete'
      && entries.length === 0
      && !currentStrategy,
  )

  return (
    <div className="page-stack governance-overview">
      <header className="page-header governance-overview-header">
        <div>
          <p className="eyebrow">Overview</p>
          <h1>AI governance at {current.organisation.name}</h1>
          <p>See how AI is being used, what needs attention, and how your strategy and roadmap are progressing.</p>
        </div>
      </header>

      {anyLoadError ? (
        <div className="inline-alert neutral">Some overview information could not be loaded. The underlying areas remain available from the navigation.</div>
      ) : null}

      <MyGovernanceTasks />

      <section className="governance-metric-grid" aria-label="Organisation AI governance position">
        {hasRegister ? (
          <Link className="governance-metric-card" to="/ai-register">
            <span className="metric-icon" aria-hidden="true"><Icon name="register" size={18} /></span>
            <span className="metric-label">AI uses</span>
            <strong>{registerSummaryQuery.isLoading ? '—' : summary?.total ?? 0}</strong>
            <small>{summary?.liveOrPilot ?? 0} live or in pilot</small>
          </Link>
        ) : null}

        {hasRegister ? (
          <Link className={`governance-metric-card${(summary?.needsAttention ?? 0) > 0 ? ' attention' : ''}`} to="/ai-register">
            <span className="metric-icon" aria-hidden="true"><Icon name="shield" size={18} /></span>
            <span className="metric-label">Needs attention</span>
            <strong>{registerSummaryQuery.isLoading ? '—' : summary?.needsAttention ?? 0}</strong>
            <small>{summary?.ownerOrInformationMissing ?? 0} with ownership or information gaps</small>
          </Link>
        ) : null}

        {hasRegister ? (
          <Link className={`governance-metric-card${count(riskSummary.outsideTolerance) > 0 ? ' attention' : ''}`} to="/ai-risks">
            <span className="metric-icon" aria-hidden="true"><Icon name="alert" size={18} /></span>
            <span className="metric-label">Current AI risks</span>
            <strong>{riskSummaryQuery.isLoading ? '—' : count(riskSummary.totalCurrent)}</strong>
            <small>{count(riskSummary.outsideTolerance)} outside tolerance</small>
          </Link>
        ) : null}

        {hasPortfolio && canReadPortfolio ? (
          <Link className="governance-metric-card" to="/strategy">
            <span className="metric-icon" aria-hidden="true"><Icon name="strategy" size={18} /></span>
            <span className="metric-label">AI strategy</span>
            <strong className="metric-text-value">{strategyQuery.isLoading ? 'Loading' : currentStrategy ? 'Current' : 'Not set'}</strong>
            <small>{currentStrategy ? `${currentStrategy.objectives.filter((objective) => objective.status === 'active').length} strategic objectives` : 'Set the organisation’s AI direction'}</small>
          </Link>
        ) : null}

        {hasPortfolio && canReadPortfolio ? (
          <Link className="governance-metric-card" to="/roadmap">
            <span className="metric-icon" aria-hidden="true"><Icon name="roadmap" size={18} /></span>
            <span className="metric-label">AI roadmap</span>
            <strong>{initiativesQuery.isLoading ? '—' : activeInitiatives.length}</strong>
            <small>{inProgressInitiatives.length} currently in progress</small>
          </Link>
        ) : null}
      </section>

      {hasRegister ? <GovernancePulse pulse={governancePulseQuery.data} loading={governancePulseQuery.isLoading} /> : null}

      {hasRegister && !registerIsEmpty ? <section className="panel overview-risk-reporting" aria-labelledby="overview-risk-reporting-heading">
        <div className="section-heading compact"><div><p className="eyebrow">AI Risk reporting</p><h2 id="overview-risk-reporting-heading">Current portfolio exposure</h2><p>Reusable Release 1 reporting data for management now and the separate Board Pack work.</p></div><Link className="text-link" to="/ai-risks">Open AI Risks</Link></div>
        <div className="risk-summary-grid">
          <article><span>High or Extreme untreated</span><strong>{riskSummaryQuery.isLoading ? '—' : count(riskSummary.highExtremeUntreated)}</strong></article>
          <article><span>Awaiting Risk Acceptance</span><strong>{riskSummaryQuery.isLoading ? '—' : count(riskSummary.awaitingAcceptance)}</strong></article>
          <article><span>Control / evidence gaps</span><strong>{riskSummaryQuery.isLoading ? '—' : count(riskSummary.incompleteControlOrEvidence)}</strong></article>
          <article><span>Stale accepted risks</span><strong>{riskSummaryQuery.isLoading ? '—' : count(riskSummary.staleAcceptedRisks)}</strong></article>
        </div>
      </section> : null}

      {registerIsEmpty ? (
        <section className="panel overview-guidance-panel">
          <EmptyState
            icon="register"
            eyebrow="Start here"
            title="Record your organisation’s first AI use"
            description="Tell Swell which product is being used and what it is being used for. Plain-English questions will identify the governance work that follows."
            actions={<Link className="button primary" to="/ai-register">Add an AI use</Link>}
          />
        </section>
      ) : null}

      <section className="overview-two-column">
        <div className="panel overview-attention-panel">
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">Requires attention</p>
              <h2>What management should look at now</h2>
            </div>
            {hasRegister ? <Link className="text-link" to="/ai-register">View AI Register</Link> : null}
          </div>

          {!hasRegister ? (
            <EmptyState compact icon="shield" title="AI Register is not available" description="Governance attention will appear here when the AI Register is enabled for this organisation." />
          ) : registerEntriesQuery.isLoading ? (
            <p className="overview-loading">Checking recorded AI uses…</p>
          ) : attentionEntries.length === 0 ? (
            <div className="overview-clear-state">
              <span className="status-indicator positive" aria-hidden="true"><Icon name="shield" size={18} /></span>
              <div>
                <strong>No immediate attention signals in the recorded AI uses</strong>
                <p>This reflects the information currently recorded in Swell. New uses, missing information or material changes can change the position.</p>
              </div>
            </div>
          ) : (
            <div className="overview-attention-list">
              {attentionEntries.map((entry) => (
                <Link key={entry.id} to={`/ai-register/${entry.id}`}>
                  <div>
                    <strong>{entry.title}</strong>
                    <span>{entry.businessUnitName ?? 'Business area needs confirmation'}</span>
                  </div>
                  <div className="overview-attention-status">
                    {entry.governanceAttention === 'attention_needed' ? <span className="status-pill attention">Needs attention</span> : null}
                    {entry.ownerOrInformationMissing ? <span className="status-pill incomplete">Information incomplete</span> : null}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="panel overview-next-panel">
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">Next useful step</p>
              <h2>Keep the framework moving</h2>
            </div>
          </div>
          <div className="overview-next-list">
            {showOnboardingRecommendation && onboardingRecommendation ? (
              <Link to={onboardingRecommendation.path}>
                <span className="next-step-icon" aria-hidden="true"><Icon name={onboardingRecommendation.key === 'known_ai_uses' || onboardingRecommendation.key === 'first_ai_use' ? 'register' : onboardingRecommendation.key === 'strategy' ? 'strategy' : 'shield'} size={18} /></span>
                <span><strong>{onboardingRecommendation.title}</strong><small>{onboardingRecommendation.reason}</small></span>
                <Icon name="arrow-right" size={16} />
              </Link>
            ) : (
              <>
                {hasRegister ? (
                  <Link to="/ai-register">
                    <span className="next-step-icon" aria-hidden="true"><Icon name="register" size={18} /></span>
                    <span><strong>{registerIsEmpty ? 'Record your first AI use' : 'Review the AI Register'}</strong><small>{registerIsEmpty ? 'Start with an identifiable business use and confirm details later.' : 'Keep ownership, lifecycle and governance attention current.'}</small></span>
                    <Icon name="arrow-right" size={16} />
                  </Link>
                ) : null}
                {hasPortfolio && canReadPortfolio ? (
                  <Link to={currentStrategy ? '/roadmap' : '/strategy'}>
                    <span className="next-step-icon" aria-hidden="true"><Icon name={currentStrategy ? 'roadmap' : 'strategy'} size={18} /></span>
                    <span><strong>{currentStrategy ? (activeInitiatives.length ? 'Review the AI roadmap' : 'Build the AI roadmap') : 'Set the AI strategy'}</strong><small>{currentStrategy ? 'Connect planned AI initiatives to strategic objectives and real AI uses.' : 'Define the organisation’s direction before measuring AI activity against it.'}</small></span>
                    <Icon name="arrow-right" size={16} />
                  </Link>
                ) : null}
              </>
            )}
          </div>
        </div>
      </section>

      {hasRegister && !registerIsEmpty ? (
        <section className="panel overview-recent-panel">
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">Recent AI Register changes</p>
              <h2>What has changed recently</h2>
            </div>
          </div>
          {recentEntries.length ? (
            <div className="overview-recent-list">
              {recentEntries.map((entry) => (
                <Link key={entry.id} to={`/ai-register/${entry.id}`}>
                  <span><strong>{entry.title}</strong><small>{entry.businessOwnerName ?? 'Owner needs assignment'}</small></span>
                  <span>{formatDate(entry.lastMaterialUpdateAt)}</span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="overview-loading">Recent changes will appear here as the AI Register is established.</p>
          )}
        </section>
      ) : null}
    </div>
  )
}
